import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, useTexture } from '@react-three/drei';
import * as THREE from 'three';

const EarthSystem = () => {
  const systemRef = useRef<THREE.Group>(null);
  const earthGroupRef = useRef<THREE.Group>(null);
  const earthMeshRef = useRef<THREE.Mesh>(null);
  
  const earthMap = useTexture('/textures/earth.jpg');
  
  const orbitRadius = 6;
  const tilt = (23.5 * Math.PI) / 180; // 23.5 degrees in radians

  useFrame((state, delta) => {
    if (systemRef.current && earthGroupRef.current && earthMeshRef.current) {
      // 1. Orbit the entire system slowly
      systemRef.current.rotation.y += delta * 0.2;
      
      // 2. Earth rotation (day/night cycle)
      earthMeshRef.current.rotation.y += delta * 2;
      
      // 3. Keep the tilt direction absolute (pointing to the "stars")
      // Since systemRef rotates, we counter-rotate the earthGroup before applying tilt
      // Wait, standard way to do absolute tilt in a revolving group is:
      // Translate -> counter-rotate orbit -> apply absolute tilt.
      
      earthGroupRef.current.position.set(orbitRadius, 0, 0);
      earthGroupRef.current.rotation.y = -systemRef.current.rotation.y;
    }
  });

  return (
    <group ref={systemRef}>
      {/* Earth translated to orbit radius, counter-rotated, and tilted */}
      <group ref={earthGroupRef}>
        <group rotation={[0, 0, tilt]}>
          <mesh ref={earthMeshRef}>
            <sphereGeometry args={[1, 32, 32]} />
            <meshStandardMaterial map={earthMap} roughness={0.7} />
          </mesh>
          {/* Axis Line */}
          <mesh position={[0, 0, 0]}>
            <cylinderGeometry args={[0.02, 0.02, 3, 8]} />
            <meshBasicMaterial color="#FFFFFF" />
          </mesh>
        </group>
      </group>
    </group>
  );
};

const Sun = () => {
  return (
    <mesh>
      <sphereGeometry args={[1.5, 32, 32]} />
      <meshBasicMaterial color="#FDE047" />
      {/* Sun glow can be simulated with pointlight */}
      <pointLight color="#FFF5E6" intensity={200} distance={50} decay={2} />
    </mesh>
  );
};

const OrbitPath = () => {
  const points = useMemo(() => {
    const pts = [];
    for(let i=0; i<=64; i++) {
      const angle = (i / 64) * Math.PI * 2;
      pts.push(new THREE.Vector3(Math.cos(angle) * 6, 0, Math.sin(angle) * 6));
    }
    return pts;
  }, []);
  
  const geo = new THREE.BufferGeometry().setFromPoints(points);
  
  return (
    <line geometry={geo}>
      <lineBasicMaterial color="#ffffff" transparent opacity={0.2} />
    </line>
  );
};

export const EarthTiltOrbital: React.FC<{ width?: number | string; height?: number | string }> = ({ width = '100%', height = 400 }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width, background: '#0F172A', padding: '16px', borderRadius: '12px', color: 'white' }}>
      <div style={{ width: '100%', height: Number(height) - 40, borderRadius: '8px', overflow: 'hidden' }}>
        <Canvas camera={{ position: [0, 4, 12], fov: 45 }}>
          <ambientLight intensity={0.05} /> {/* Dim ambient to show day/night clearly */}
          
          <Sun />
          <OrbitPath />
          <EarthSystem />
          
          <OrbitControls enableZoom={true} enablePan={false} autoRotate={false} />
        </Canvas>
      </div>
      <div style={{ marginTop: '12px', fontSize: '13px', color: '#94A3B8', textAlign: 'center' }}>
        Notice how the North Pole always points the same way in space.
      </div>
    </div>
  );
};
